<template>
  <div
    v-if="getContentByType.component"
    class="w-screen md:max-w-(--breakpoint-xl) mx-auto section-y px-4"
  >
    <component
      :is="getContentByType.component"
      v-bind="getContentByType.attributes"
    />

    <div v-if="content?.CTA?.length > 0" class="flex justify-center gap-2 mt-8">
      <Button
        v-for="button in content?.CTA"
        :key="button?.label"
        :link="button?.url"
        :color="button?.color ?? 'primary'"
        class="px-4.5! py-2.5!"
        @click="handleCTA(button?.url)"
      >
        {{ button?.label }}
      </Button>
    </div>
  </div>
</template>
<script setup lang="ts">
import { useHomepageSearchEngine } from '~/composables/useHomepageSearchEngine'
import { eventSectionComposableFactory } from '~/composables/algolia/states'
import { GtmItemListId } from '~/composables/useKftGtm/types'
import { useAlgoliaSearchInsights } from '~/composables/algolia/useAlgoliaSearchInsights'

const {
  content = {},
  showMoreInTitle = false,
  maxRows = undefined,
  maxProducts = undefined,
} = defineProps<{
  content: any
  /**
   * Moves "show more" beside the section title instead of the default button
   * below the grid — see `ProductShowcase.showMoreInTitle`. Set per page.
   */
  showMoreInTitle?: boolean
  /** Caps the showcase to whole grid rows — see `ProductShowcase.maxRows`. */
  maxRows?: number
  /** Hard cap on rendered products — see `ProductShowcase.maxProducts`. */
  maxProducts?: number
}>()

const { t, locale } = useI18n()
const { client: insightsClient } = useAlgoliaSearchInsights()
const { toggleDateRequestSidebar } = useUiState()
const route = useRoute()
const emit = defineEmits(['openContactForm', 'openCaseStudyForm'])

const { algoliaResults } =
  content.listType !== 'boxes' ? await useHomepageSearchEngine() : {}
const useTopEvents = eventSectionComposableFactory(0)
const useTopGiftIdeasEvents = eventSectionComposableFactory(2)

const { data: topEvents, queryId: topEventsQueryID } =
  content.listType === 'events'
    ? useTopEvents(algoliaResults)
    : { data: [], queryId: ref(null) }
const { data: topGiftIdeasEvents, queryId: topGiftIdeasEventsQueryID } =
  content.listType === 'giftcards'
    ? useTopGiftIdeasEvents(algoliaResults)
    : { data: [], queryId: ref(null) }

const filteredGiftIdeas = computed(() => {
  if (!topGiftIdeasEvents.value || !topEvents.value) return []

  const topEventIds = new Set(
    topEvents.value.map((event) => event?.objectID).filter(Boolean)
  )

  return topGiftIdeasEvents.value
    .filter((event) => event?.objectID && !topEventIds.has(event.objectID))
    .slice(0, 10)
})

const eventsProps = computed(() => {
  const defaults = {
    collapseProducts: true,
    dataTestId: 'top-events-section',
    imageLoading: 'lazy',
    products: topEvents.value,
    gtmItemListId: GtmItemListId.GeneralPageTopEvents,
    queryId: topEventsQueryID.value,
    algoliaInsightsClient: insightsClient,
    algoliaIndexName: getTopEventListIndexName(locale.value),
    tags: [],
    title: content.title || t('home.browseTopEvents'),
    algoliaInsightsLabel: 'Top Events - Strapi',
    titleHClass: 'section-title',
  }

  if (content.listType === 'eventsRecommended') {
    return {
      ...defaults,
      dataTestId: 'recommended-events-section',
      products: filteredGiftIdeas.value,
      queryId: topGiftIdeasEventsQueryID.value,
      gtmItemListId: GtmItemListId.GeneralPageTopGiftIdeas,
      algoliaIndexName: getTopGiftIdeasIndexName(locale.value),
      title: content.title || t('home.recommendedForYou'),
      algoliaInsightsLabel: 'Gift Event Ideas - Strapi',
    }
  }

  return defaults
})

/** CMS opt-in to a horizontal rail instead of a grid, for every list type. */
const isSlider = computed(() => content?.isSlider ?? false)

const getContentByType = computed(() => {
  const type = content?.listType?.toLowerCase()

  if (!type) return {}

  if (type === 'boxes') {
    return {
      component: resolveComponent('ProductBoxShowcase'),
      attributes: {
        title: content?.title || t('home.inspirationForYou'),
        subtitle: content?.subtitle,
        isDurationVisible: content?.isDurationVisible ?? true,
        isSlider: isSlider.value,
        showMoreInTitle: showMoreInTitle && !isSlider.value,
        expandable: true,
        category: content?.category,
        tag: content?.tag,
        count: content?.count,
        itemsPerPage: 1000,
        showItemsOnDesktop: 8,
        maxRows,
        maxProducts,
      },
    }
  }

  if (type === 'giftcards') {
    return {
      component: resolveComponent('ProductGiftcardEventsShowcase'),
      attributes: {
        title: content?.title || t('home.inspirationForYou'),
        subtitle: content?.subtitle,
        isDurationVisible: content?.isDurationVisible ?? true,
        isSlider: isSlider.value,
        showMoreInTitle: showMoreInTitle && !isSlider.value,
        expandable: true,
        maxRows,
        maxProducts,
      },
    }
  }

  return {
    component: resolveComponent('ProductShowcase'),
    attributes: {
      ...eventsProps.value,
      subtitle: content?.subtitle,
      isDurationVisible: content.isDurationVisible ?? true,
      isSlider: isSlider.value,
      showMoreInTitle: showMoreInTitle && !isSlider.value,
      expandable: true,
      maxRows,
      maxProducts,
    },
  }
})

const handleCTA = (action: string) => {
  if (action === 'openEventRequestForm') {
    toggleDateRequestSidebar(route.name?.toString(), 'strapi_component')
  } else if (action === 'openPartnerContactForm') {
    emit('openContactForm')
  } else if (action === 'openCaseStudyForm') {
    emit('openCaseStudyForm')
  }
}
</script>

<style scoped lang="css"></style>
